import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  CircularProgress,
  Drawer,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Edit as EditIcon,
  MailOutline as MailIcon,
  PersonOff as PersonOffIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import { USER_STATUS_STYLES, getAccountStatusLabel } from "../constants";
import { formatDateTimeParts } from "../helpers/formatDateTimeParts";
import { getUserId } from "../../../services/types/userPlanTypes";

const PAYMENT_METHOD_LABELS = {
  monthly: "Mensual",
  annual: "Anual",
};

const getPaymentMethodLabel = (value) =>
  PAYMENT_METHOD_LABELS[value] ?? value ?? "-";

const getPlanStatusLabel = (statusPlan) => (statusPlan ? "Activo" : "Inactivo");

const resolveCompanyName = (user) => {
  if (!user) return "";
  const candidates = [
    user.company,
    user.companyName,
    user.company_name,
    user.empresa,
    user.user?.company,
    user.user?.companyName,
    user.user?.company_name,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
    if (candidate && typeof candidate === "object") {
      const name = candidate.name ?? candidate.companyName ?? candidate.company_name;
      if (name && String(name).trim()) return String(name).trim();
    }
  }

  return "";
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ??
  error?.response?.data?.error ??
  error?.message ??
  fallback;

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    {typeof value === "string" || typeof value === "number" ? (
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
    ) : (
      value
    )}
  </Box>
);

const DateTimeCell = ({ value, fallbackValue }) => {
  const parts = formatDateTimeParts(value);

  if (!parts) {
    return (
      <Typography variant="body2" fontWeight={500}>
        {fallbackValue}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25} alignItems="flex-end">
      <Typography variant="body2" fontWeight={500}>
        {parts.date}
      </Typography>
      {parts.time ? (
        <Typography variant="caption" color="text.secondary">
          {parts.time}
        </Typography>
      ) : null}
    </Stack>
  );
};

const UserDetailDrawer = ({
  user,
  loadingDetail = false,
  open,
  onClose,
  onUpdateUser,
  onUpdateUserPlan,
  onDeactivateUser,
  onCreateUserPlan,
  onRenewUserPlan,
}) => {
  const FALLBACK_VALUE = "-";
  const getDisplayValue = (value) => {
    if (value === null || value === undefined) return FALLBACK_VALUE;
    if (typeof value === "string" && !value.trim()) return FALLBACK_VALUE;
    return value;
  };

  const [notes, setNotes] = React.useState(user?.notes ?? "");
  const [saving, setSaving] = React.useState(false);

  const [openCreatePlanDialog, setOpenCreatePlanDialog] = React.useState(false);
  const [openPlanDialog, setOpenPlanDialog] = React.useState(false);
  const [openDesactivateDialog, setOpenDesactivateDialog] = React.useState(false);
  const [createPlanValue, setCreatePlanValue] = React.useState(user?.plan ?? "");
  const [createPaymentMethod, setCreatePaymentMethod] = React.useState("monthly");
  const [createSource, setCreateSource] = React.useState(user?.source ?? "Directo");
  const [createPlanNotes, setCreatePlanNotes] = React.useState(user?.notes ?? "");
  const [renewPlanValue, setRenewPlanValue] = React.useState(user?.plan ?? "");
  const [renewPaymentMethod, setRenewPaymentMethod] = React.useState(
    user?.paymentMethod ?? "monthly"
  );
  const [renewStatusPlan, setRenewStatusPlan] = React.useState(
    String(user?.statusPlan ?? true)
  );
  const [renewSource, setRenewSource] = React.useState(user?.source ?? "");
  const [renewPlanNotes, setRenewPlanNotes] = React.useState(user?.notes ?? "");
  const [feedback, setFeedback] = React.useState({
    open: false,
    severity: "success",
    message: "",
  });

  const resolvedUserId = user ? getUserId(user) : null;

  const showFeedback = (severity, message) => {
    setFeedback({ open: true, severity, message });
  };

  const handleCloseFeedback = (_event, reason) => {
    if (reason === "clickaway") return;
    setFeedback((prev) => ({ ...prev, open: false }));
  };
  const companyName = user ? resolveCompanyName(user) : "";
  const accountStatusLabel = user ? getAccountStatusLabel(user.active) : "-";
  const planStatusLabel = user ? getPlanStatusLabel(user.statusPlan) : "-";

  const syncPlanFormFromUser = React.useCallback((planUser) => {
    if (!planUser) return;
    setNotes(planUser.notes ?? "");
    setCreatePlanValue(planUser.plan ?? "");
    setCreatePaymentMethod(planUser.paymentMethod || "monthly");
    setCreateSource(planUser.source ?? "Directo");
    setCreatePlanNotes(planUser.notes ?? "");
    setRenewPlanValue(planUser.plan ?? "");
    setRenewPaymentMethod(planUser.paymentMethod || "monthly");
    setRenewStatusPlan(String(planUser.statusPlan ?? true));
    setRenewSource(planUser.source ?? "");
    setRenewPlanNotes(planUser.notes ?? "");
  }, []);

  React.useEffect(() => {
    syncPlanFormFromUser(user);
  }, [user, syncPlanFormFromUser]);

  const PLAN_OPTIONS = [
    { label: "Free", egresos: 79, price: "usd0", isEnterprise: false },
    { label: "Starter", egresos: 699, price: "usd9", isEnterprise: false },
    { label: "Standard", egresos: 1499, price: "usd16", isEnterprise: false },
    {
      label: "Professional",
      egresos: 3299,
      price: "usd28",
      isEnterprise: false,
    },
    {
      label: "Enterprise",
      egresos: 3300,
      price: "precio a medida",
      isEnterprise: true,
    },
  ];
  const PAYMENT_METHOD_OPTIONS = [
    { value: "monthly", label: "Mensual" },
    { value: "annual", label: "Anual" },
  ];
  const STATUS_PLAN_OPTIONS = [
    { value: "true", label: "Activo" },
    { value: "false", label: "Inactivo" },
  ];

  const selectedCreatePlan = PLAN_OPTIONS.find(
    (plan) => plan.label === createPlanValue,
  );
  const selectedRenewPlan = PLAN_OPTIONS.find(
    (plan) => plan.label === renewPlanValue,
  );
  const currentPlan = PLAN_OPTIONS.find((plan) => plan.label === user?.plan);
  const currentPlanTokens = currentPlan
    ? `${currentPlan.isEnterprise ? "Desde" : "Hasta"} ${currentPlan.egresos
    } egresos`
    : FALLBACK_VALUE;

  const submitCreatePlan = async () => {
    if (!user || !createPlanValue.trim()) return;
    setSaving(true);
    try {
      await onCreateUserPlan?.({
        user_id: resolvedUserId,
        plan: createPlanValue.trim(),
        payment_method: createPaymentMethod?.trim() || "monthly",
        source: createSource?.trim() || "Directo",
        notes: createPlanNotes?.trim() || "",
      });
      setOpenCreatePlanDialog(false);
      showFeedback("success", "El plan se creó exitosamente.");
    } catch (error) {
      showFeedback(
        "error",
        getErrorMessage(error, "No se pudo crear el plan. Intentá nuevamente.")
      );
    } finally {
      setSaving(false);
    }
  };

  const submitPlanUpdate = async () => {
    if (!user || !renewPlanValue.trim()) return;
    setSaving(true);
    try {
      await onRenewUserPlan?.(user, {
        plan: renewPlanValue.trim(),
        payment_method: renewPaymentMethod?.trim() || user.paymentMethod,
        status_plan: renewStatusPlan === "true",
        source: renewSource?.trim() || user.source,
        notes: renewPlanNotes?.trim() ?? user.notes,
      });
      setOpenPlanDialog(false);
      showFeedback("success", "El plan se modificó exitosamente.");
    } catch (error) {
      showFeedback(
        "error",
        getErrorMessage(error, "No se pudo modificar el plan. Intentá nuevamente.")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!user || notes === (user.notes ?? "")) return;
    setSaving(true);
    try {
      await onRenewUserPlan?.(user, { notes: notes.trim() });
      showFeedback("success", "Las notas del plan se guardaron exitosamente.");
    } catch (error) {
      showFeedback(
        "error",
        getErrorMessage(error, "No se pudieron guardar las notas. Intentá nuevamente.")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDesactivate = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await onDeactivateUser?.(resolvedUserId);
      setOpenDesactivateDialog(false);
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 380, display: "flex", flexDirection: "column" },
      }}
    >
      <Box
        sx={{
          p: 2.5,
          borderBottom: "1px solid",
          borderColor: "grey.200",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ minWidth: 0, pr: 1 }}>
          <Typography variant="h6" fontWeight={700} noWrap>
            {companyName || "Ficha de usuario"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {companyName ? "Ficha de usuario" : getDisplayValue(user?.email)}
          </Typography>
        </Box>
        <Button size="small" onClick={onClose} disabled={saving || loadingDetail}>
          Cerrar
        </Button>
      </Box>

      {user && loadingDetail && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}

      {user && !loadingDetail && (
        <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                letterSpacing: 2,
                fontWeight: 700,
              }}
            >
              Datos del cliente
            </Typography>
            <Stack spacing={1.5} mt={1.5}>
              <DetailRow
                label="Empresa"
                value={getDisplayValue(companyName)}
              />
              <DetailRow
                label="Email"
                value={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MailIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="primary">
                      {getDisplayValue(user.email)}
                    </Typography>
                  </Stack>
                }
              />
              <DetailRow
                label="Fecha alta"
                value={
                  <DateTimeCell
                    value={user.createdAt || user.joinDate}
                    fallbackValue={getDisplayValue(user.createdAt || user.joinDate)}
                  />
                }
              />
            </Stack>
          </Box>

          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, mb: 3, bgcolor: "grey.50" }}
          >
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                letterSpacing: 2,
                fontWeight: 700,
              }}
            >
              Información del plan
            </Typography>
            <Stack spacing={1.5} mt={1.5}>
              <DetailRow
                label="Plan actual"
                value={
                  user.plan ? (
                    <Chip
                      label={user.plan}
                      size="small"
                      color="primary"
                      sx={{
                        fontSize: 10,
                        textTransform: "uppercase",
                        fontWeight: 700,
                      }}
                    />
                  ) : (
                    FALLBACK_VALUE
                  )
                }
              />
              <DetailRow
                label="Estado cuenta"
                value={
                  <Chip
                    label={accountStatusLabel}
                    size="small"
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      ...(USER_STATUS_STYLES[accountStatusLabel] ??
                        USER_STATUS_STYLES.Activo),
                    }}
                  />
                }
              />
              <DetailRow
                label="Estado plan"
                value={
                  <Chip
                    label={planStatusLabel}
                    size="small"
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      ...(USER_STATUS_STYLES[planStatusLabel] ??
                        USER_STATUS_STYLES.Activo),
                    }}
                  />
                }
              />
              <DetailRow
                label="Método de pago"
                value={getPaymentMethodLabel(user.paymentMethod)}
              />
              <DetailRow label="Canal" value={getDisplayValue(user.source)} />
              <DetailRow label="Egresos" value={currentPlanTokens} />
              <DetailRow
                label="Vencimiento"
                value={
                  <DateTimeCell
                    value={user.expiryDate}
                    fallbackValue={getDisplayValue(user.expiryDate)}
                  />
                }
              />
              <DetailRow
                label="Último pago"
                value={
                  <DateTimeCell
                    value={user.lastPayment}
                    fallbackValue={getDisplayValue(user.lastPayment)}
                  />
                }
              />
              <DetailRow
                label="Última actualización"
                value={
                  <DateTimeCell
                    value={user.updatedAt}
                    fallbackValue={getDisplayValue(user.updatedAt)}
                  />
                }
              />
            </Stack>
          </Paper>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                letterSpacing: 2,
                fontWeight: 700,
              }}
            >
              Observaciones internas
            </Typography>
            <TextField
              multiline
              minRows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={handleSaveNotes}
              placeholder="Escribe notas administrativas aquí..."
              fullWidth
              disabled={saving}
              sx={{ mt: 1.5 }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Se guarda al salir del campo
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Stack spacing={1}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<EditIcon fontSize="small" />}
              sx={{ textTransform: "none" }}
              onClick={() => setOpenCreatePlanDialog(true)}
              disabled={saving}
            >
              Crear plan
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<EditIcon fontSize="small" />}
              sx={{ textTransform: "none" }}
              onClick={() => {
                syncPlanFormFromUser(user);
                setOpenPlanDialog(true);
              }}
              disabled={saving || !user?.id}
            >
              Renovar plan
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              startIcon={<PersonOffIcon fontSize="small" />}
              sx={{
                textTransform: "none",
                borderColor: "error.light",
                bgcolor: "error.50",
                "&:hover": {
                  borderColor: "error.main",
                  bgcolor: "error.100",
                },
              }}
              onClick={() => setOpenDesactivateDialog(true)}
              disabled={saving || user?.active === false}
            >
              Dar de baja
            </Button>
          </Stack>
        </Box>
      )}

      <Dialog
        open={openCreatePlanDialog}
        onClose={() => !saving && setOpenCreatePlanDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Crear plan</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Plan"
              value={createPlanValue}
              onChange={(e) => setCreatePlanValue(e.target.value)}
              fullWidth
              disabled={saving}
              helperText={
                selectedCreatePlan
                  ? `${selectedCreatePlan.egresos} egresos disponibles`
                  : " "
              }
            >
              {PLAN_OPTIONS.map((option) => (
                <MenuItem key={option.label} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Método de pago"
              value={createPaymentMethod}
              onChange={(e) => setCreatePaymentMethod(e.target.value)}
              fullWidth
              disabled={saving}
            >
              {PAYMENT_METHOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Source"
              value={createSource}
              onChange={(e) => setCreateSource(e.target.value)}
              fullWidth
              disabled={saving}
            />
            <TextField
              label="Notes"
              value={createPlanNotes}
              onChange={(e) => setCreatePlanNotes(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              disabled={saving}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenCreatePlanDialog(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={submitCreatePlan}
            disabled={saving || !createPlanValue.trim()}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openPlanDialog}
        onClose={() => !saving && setOpenPlanDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Renovar plan</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Plan"
              value={renewPlanValue}
              onChange={(e) => setRenewPlanValue(e.target.value)}
              fullWidth
              disabled={saving}
              SelectProps={{
                renderValue: (selected) => {
                  const plan = PLAN_OPTIONS.find(
                    (option) => option.label === selected,
                  );
                  if (!plan) return selected;
                  return `${plan.label} — ${plan.price} · ${
                    plan.isEnterprise ? "Desde" : "Hasta"
                  } ${plan.egresos} egresos`;
                },
              }}
              helperText={
                selectedRenewPlan
                  ? `${selectedRenewPlan.egresos} egresos disponibles`
                  : " "
              }
            >
              {PLAN_OPTIONS.map((option) => (
                <MenuItem key={option.label} value={option.label}>
                  <Stack spacing={0.25} sx={{ width: "100%" }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {option.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="primary"
                        fontWeight={600}
                      >
                        {option.price}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {option.isEnterprise
                        ? `Desde ${option.egresos} egresos / mes`
                        : `Hasta ${option.egresos} egresos / mes`}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      Incluye todas las funcionalidades
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Método de pago"
              value={renewPaymentMethod}
              onChange={(e) => setRenewPaymentMethod(e.target.value)}
              fullWidth
              disabled={saving}
            >
              {PAYMENT_METHOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Estado del plan"
              value={renewStatusPlan}
              onChange={(e) => setRenewStatusPlan(e.target.value)}
              fullWidth
              disabled={saving}
            >
              {STATUS_PLAN_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Canal"
              value={renewSource}
              onChange={(e) => setRenewSource(e.target.value)}
              fullWidth
              disabled={saving}
            />
            <TextField
              label="Notas"
              value={renewPlanNotes}
              onChange={(e) => setRenewPlanNotes(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              disabled={saving}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenPlanDialog(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={submitPlanUpdate}
            disabled={saving || !renewPlanValue.trim()}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDesactivateDialog}
        onClose={() => !saving && setOpenDesactivateDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: "error.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PersonOffIcon color="error" />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Dar de baja usuario
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Esta acción restringe el acceso al sistema
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert
            severity="warning"
            icon={<WarningAmberIcon fontSize="inherit" />}
            sx={{ mb: 2, borderRadius: 1.5 }}
          >
            El usuario no podrá iniciar sesión hasta que se reactive desde
            administración.
          </Alert>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 1.5,
              bgcolor: "grey.50",
            }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              {getDisplayValue(companyName || resolveCompanyName(user))}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
              <MailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary">
                {getDisplayValue(user?.email)}
              </Typography>
            </Stack>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <Button
            onClick={() => setOpenDesactivateDialog(false)}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDesactivate}
            disabled={saving}
            startIcon={saving ? null : <PersonOffIcon />}
            sx={{ textTransform: "none", px: 2.5 }}
          >
            {saving ? "Dando de baja..." : "Confirmar baja"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseFeedback}
          severity={feedback.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Drawer>
  );
};

export default UserDetailDrawer;